

import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import styles from './BotResultView.module.css';
import { UniversalChatChart } from '../chart/UniversalChatChart';


function downloadCSV(csv, filename = 'data.csv') {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// Robust CSV parser
function parseCSV(csv) {
  if (typeof csv !== 'string') return { headers: [], rows: [] };
  const lines = csv.trim().split('\n');
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(',');
  const rows = lines.slice(1).map(line => line.split(','));
  return { headers, rows };
}

function DataTableWithPagination({ csv }) {
  const { headers, rows } = parseCSV(csv);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const paginatedRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalPages = Math.ceil(rows.length / rowsPerPage);

  return (
    <div className={styles.dataTableContainer}>
      <div className={styles.csvDownloadRow}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownloadIcon />}
          className={styles.csvDownloadBtn}
          onClick={() => downloadCSV(csv)}
        >
          Download CSV
        </Button>
      </div>
      <div className={styles.customTableScroll}>
        <table className={styles.customTable}>
          <thead>
            <tr>
              {headers.map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, idx) => (
              <tr key={idx}>
                {row.map((cell, cid) => (
                  <td key={cid}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.customPagination}>
        <IconButton
          onClick={() => setPage(p => Math.max(p - 1, 0))}
          disabled={page === 0}
          aria-label="Previous Page"
          color="primary"
          size="large"
          className={styles.iconBtn}
        >
          <ChevronLeftIcon fontSize="inherit" />
        </IconButton>
        <span>
          Page <b>{page + 1}</b> of <b>{totalPages}</b>
        </span>
        <IconButton
          onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
          disabled={page >= totalPages - 1}
          aria-label="Next Page"
          color="primary"
          size="large"
          className={styles.iconBtn}
        >
          <ChevronRightIcon fontSize="inherit" />
        </IconButton>
      </div>
    </div>
  );
}

export default function BotResultView({ result }) {
  // Flexible compatibility with both old/new API shapes
  const gemini = result.gemini || result || {};
  const csv = gemini.csv || result.csv;
  const chartSpec = result.primary_chart || (result.visualization && result.visualization.primary_chart);

  return (
    <div className={styles.resultRoot}>
      {/* Uncomment if you want to show the SQL */}
      {/* {gemini.query && (
        <div className={styles.sqlBlock}>
          <strong>Generated SQL Query:</strong>
          <pre className={styles.sqlPre}>{gemini.query}</pre>
        </div>
      )} */}
      {csv && <DataTableWithPagination csv={csv} />}
      {/* Visualization shown only if both present */}
      {csv && chartSpec && (
        <div className={styles.visualizationBlock}>
          <UniversalChatChart chartSpec={chartSpec} csv={csv} />
        </div>
      )}
    </div>
  );
}
